import React, { useContext, useEffect, useState, useCallback } from "react";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { ToggleRow } from "../../pages/Settings/components/SettingsPattern";

// feat_415 (CK48) — "Avisar feriados al cliente que consulta".
//   - Switch `holidayNoticeEnabled` (apagado por defecto).
//   - Plantilla `holidayNoticeTemplate` del aviso automático de WAIA (se usa
//     cuando ninguna IA contesta). Variables {motivo} {fecha_reapertura} {alcance}.
//   - Vista previa de lo que reciben la IA / n8n: hoy + próximos 30 días.
// Guardado: el switch al tocarlo; la plantilla en onBlur (los TabPanels de
// /Settings quedan montados — no desmontar este bloque).

const useStyles = makeStyles(theme => ({
  preview: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    background: theme.palette.type === "dark" ? "#202c33" : "#f0f2f5",
  },
  item: { fontSize: 13, padding: "3px 0", wordBreak: "break-word" },
  today: { fontWeight: 600, color: theme.palette.type === "dark" ? "#53bdeb" : "#075e54" },
  sample: {
    marginTop: 8,
    padding: "6px 10px",
    borderRadius: 8,
    fontSize: 13,
    whiteSpace: "pre-wrap",
    background: theme.palette.type === "dark" ? "#005c4b" : "#d9fdd3",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    maxWidth: 420,
  },
}));

const HolidayNoticeSettings = ({ refreshKey }) => {
  const classes = useStyles();
  const { getSetting } = useContext(SettingsContext);
  const enabledCtx = getSetting("holidayNoticeEnabled") === "true";
  const templateCtx = getSetting("holidayNoticeTemplate") || "";
  const [enabled, setEnabled] = useState(enabledCtx);
  const [template, setTemplate] = useState(templateCtx);
  const [preview, setPreview] = useState(null);

  useEffect(() => setEnabled(enabledCtx), [enabledCtx]);
  useEffect(() => setTemplate(templateCtx), [templateCtx]);

  const save = async (key, value) => {
    try {
      await api.put(`/settings/${key}`, { value });
    } catch (err) {
      toastError(err);
    }
  };

  const loadPreview = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/holidays/upcoming?days=30");
      setPreview(data);
    } catch (err) {
      setPreview(null);
    }
  }, []);

  useEffect(() => {
    loadPreview();
  }, [loadPreview, refreshKey]);

  const sampleHoliday = preview?.today || preview?.upcoming?.[0] || null;
  const sampleText = (() => {
    if (!sampleHoliday) return null;
    const base =
      (sampleHoliday.message && sampleHoliday.message.trim()) ||
      (template && template.trim()) ||
      i18n.t("holidays.noticeDefault");
    return base
      .replace(/\{motivo\}/g, sampleHoliday.name)
      .replace(/\{fecha_reapertura\}/g, sampleHoliday.reopensText || "-")
      .replace(/\{alcance\}/g, sampleHoliday.scope || "");
  })();

  const line = (h, isToday) => (
    <div key={h.date} className={`${classes.item} ${isToday ? classes.today : ""}`}>
      {isToday ? `${i18n.t("holidays.today")}: ` : ""}
      {h.weekday} {h.date.split("-").reverse().slice(0, 2).join("/")} — {h.name}
      {h.scope ? ` (${h.scope})` : ""}
      {h.reopensText ? ` · ${i18n.t("holidays.reopens", { when: h.reopensText })}` : ""}
    </div>
  );

  return (
    <div>
      <ToggleRow
        id="setting-holidayNoticeEnabled"
        label={i18n.t("holidays.noticeEnabledLabel")}
        help={i18n.t("holidays.noticeEnabledHelp")}
        tooltip={i18n.t("holidays.noticeEnabledTooltip")}
        checked={enabled}
        onChange={async e => {
          const val = e.target.checked ? "true" : "false";
          setEnabled(e.target.checked);
          await save("holidayNoticeEnabled", val);
        }}
      />
      <TextField
        label={i18n.t("holidays.noticeTemplateLabel")}
        value={template}
        onChange={e => setTemplate(e.target.value)}
        onBlur={() => save("holidayNoticeTemplate", template)}
        placeholder={i18n.t("holidays.noticeDefault")}
        helperText={i18n.t("holidays.noticeTemplateHelp")}
        InputLabelProps={{ shrink: true }}
        variant="outlined"
        size="small"
        fullWidth
        multiline
        rows={3}
        margin="dense"
        inputProps={{ maxLength: 1000 }}
      />

      <div className={classes.preview}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <Typography variant="subtitle2">{i18n.t("holidays.upcomingTitle")}</Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadPreview} style={{ textTransform: "none" }}>
            {i18n.t("holidays.refresh")}
          </Button>
        </div>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          {i18n.t("holidays.upcomingHelp")}
        </Typography>
        {preview && !preview.today && !(preview.upcoming || []).length && (
          <Typography variant="caption" color="textSecondary">
            {i18n.t("holidays.upcomingNone")}
          </Typography>
        )}
        {preview?.today && line(preview.today, true)}
        {(preview?.upcoming || []).map(h => line(h, false))}
        {sampleText && (
          <>
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 10 }}>
              {i18n.t("holidays.sampleLabel", { date: sampleHoliday.date.split("-").reverse().join("/") })}
            </Typography>
            <div className={classes.sample}>{sampleText}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default HolidayNoticeSettings;
